import {
  useMediaDeviceSelect, useTrackToggle,
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import { Check, ChevronDown } from 'lucide-react-native';
import { useState } from 'react';
import { Pressable } from 'react-native';
import {
  Box, HStack, Icon, Text,
} from '@gluestack-ui/themed';

type DeviceKind = 'audioinput' | 'videoinput';

type DevicePickerPanelProps = {
  kind: DeviceKind;
  onClose: () => void;
};

function DevicePickerPanel({ kind, onClose }: DevicePickerPanelProps) {
  const { devices, activeDeviceId, setActiveMediaDevice } = useMediaDeviceSelect({ kind });

  return (
    <Box
      position="absolute"
      bottom={52}
      left={0}
      minWidth={220}
      bg="$bgApp"
      borderRadius="$lg"
      borderWidth={1}
      borderColor="$borderLight"
      shadowColor="$shadowDefault"
      shadowOpacity={0.12}
      shadowRadius={12}
      py="$1"
      zIndex={30}
      testID={`meeting-device-panel-${kind}`}
    >
      {devices.map((device) => {
        const isActive = device.deviceId === activeDeviceId;
        return (
          <Pressable
            key={device.deviceId}
            onPress={() => {
              setActiveMediaDevice(device.deviceId);
              onClose();
            }}
          >
            <HStack
              px="$3"
              py="$2"
              space="sm"
              alignItems="center"
              bg={isActive ? '$bgSubtle' : 'transparent'}
            >
              {isActive ? (
                <Icon as={Check} size="xs" color="$primary500" />
              ) : (
                <Box w={14} />
              )}
              <Text size="sm" color="$textBody" flex={1} numberOfLines={1}>
                {device.label || device.deviceId}
              </Text>
            </HStack>
          </Pressable>
        );
      })}
    </Box>
  );
}

export type MediaPillProps = {
  source: Track.Source.Microphone | Track.Source.Camera;
  kind: DeviceKind;
  enabledIcon: typeof Check;
  disabledIcon: typeof Check;
  testID: string;
};

/** Mic/camera toggle pill with an inline device picker chevron. */
export function MediaPill({
  source, kind, enabledIcon, disabledIcon, testID,
}: MediaPillProps) {
  const [panelOpen, setPanelOpen] = useState(false);
  const { toggle, enabled } = useTrackToggle({ source });

  return (
    <Box position="relative">
      <HStack
        borderRadius="$full"
        overflow="hidden"
        borderWidth={1}
        borderColor="$borderLight"
        bg={enabled ? '$bgApp' : '$statusErrorSubtle'}
      >
        <Pressable
          onPress={() => { toggle(); }}
          testID={testID}
          accessibilityRole="button"
        >
          <Box px="$3" py="$2" alignItems="center" justifyContent="center">
            <Icon as={enabled ? enabledIcon : disabledIcon} size="sm" color="$textHeading" />
          </Box>
        </Pressable>
        <Box w={1} bg="$borderLight" />
        <Pressable
          onPress={() => setPanelOpen((v) => !v)}
          testID={`${testID}-devices`}
          accessibilityRole="button"
        >
          <Box px="$2" py="$2" alignItems="center" justifyContent="center">
            <Icon as={ChevronDown} size="sm" color="$textSubtle" />
          </Box>
        </Pressable>
      </HStack>
      {panelOpen ? (
        <DevicePickerPanel kind={kind} onClose={() => setPanelOpen(false)} />
      ) : null}
    </Box>
  );
}
